/* © ИП Коробкин И.Н., KIN DIGITAL CRM+ */
/**
 * M13. Квалифицированная подпись через КриптоПро CSP и «КриптоПро ЭЦП Browser plug-in» (расширение браузера).
 * Подпись создаётся на компьютере пользователя: закрытый ключ не покидает токен или реестр, в CRM уходит только подпись.
 * Библиотека crypto-pro (MIT) содержит официальный модуль подключения к плагину и загружается только при подписании.
 */
import { api, getAccessToken } from './api';

export interface KepCert { thumbprint: string; name: string; issuerName: string; validFrom: string; validTo: string; raw: any }

let lib: any = null;
/** Имя из сертификата без экранирования кавычек: «"ООО ""РОМАШКА"""» → «ООО "РОМАШКА"» */
export const cleanName = (n: string) => { let s = (n ?? '').trim(); if (s.startsWith('"') && s.endsWith('"') && s.length > 1) s = s.slice(1, -1); return s.replace(/""/g, '"').trim(); };
async function cp() {
  if (!lib) lib = await import('crypto-pro');
  return lib;
}

const plain = (e: unknown) => {
  const m = (e as Error)?.message ?? String(e);
  if (/plugin|плагин|cadesplugin|not loaded|недоступ/i.test(m)) return 'Не найден КриптоПро ЭЦП Browser plug-in. Откройте CRM в Chrome или Яндекс.Браузере с расширением CryptoPro и разрешите сайту доступ.';
  return m;
};

export async function kepCertificates(): Promise<KepCert[]> {
  try {
    const c = await cp();
    const list = await c.getUserCertificates(true);
    const now = Date.now();
    return list
      .filter((x: any) => !x.validTo || new Date(x.validTo).getTime() > now)
      .map((x: any) => ({ thumbprint: x.thumbprint, name: cleanName(x.name), issuerName: x.issuerName, validFrom: x.validFrom, validTo: x.validTo, raw: x }));
  } catch (e) { throw new Error(plain(e)); }
}

/** Подписать текущую (или указанную) версию документа отсоединённой подписью CAdES-BES и сохранить подпись в CRM */
export async function kepSignDocument(doc: { id: string; version?: number }, cert: KepCert, onStep?: (s: string) => void) {
  try {
    onStep?.('Загружаю файл');
    const version: number = doc.version ?? (await api.get(`/documents/${doc.id}`)).versions[0].version;
    const r = await fetch(`/api/v1/documents/${doc.id}/download?version=${version}`, { headers: { Authorization: `Bearer ${getAccessToken()}` } });
    if (!r.ok) throw new Error('Не удалось получить файл документа');
    const buf = await r.arrayBuffer();
    const c = await cp();
    const cades = (window as any).cadesplugin;
    let hashedAlgorithm: number | undefined;
    try {
      const alg = await cert.raw.getAlgorithm();
      if (String(alg?.oid ?? '').startsWith('1.2.643.7.1.1.1.2') && cades) hashedAlgorithm = cades.CADESCOM_HASH_ALGORITHM_CP_GOST_3411_2012_512;
    } catch { /* по умолчанию ГОСТ Р 34.11-2012 256 */ }
    onStep?.('Вычисляю хеш по ГОСТ');
    const hash = await c.createHash(buf, hashedAlgorithm ? { hashedAlgorithm } : undefined);
    onStep?.('Подписываю (может потребоваться PIN-код токена)');
    const signature = await c.createDetachedSignature(cert.thumbprint, hash, hashedAlgorithm ? { hashedAlgorithm } : undefined);
    let signerInfo: { title: string; description: string }[] | undefined;
    try { signerInfo = (await cert.raw.getOwnerInfo()).map((t: any) => ({ title: t.title, description: t.description })); } catch { /* без расшифровки */ }
    onStep?.('Сохраняю подпись');
    return api.post(`/documents/${doc.id}/signatures`, {
      version, signature, signerName: cert.name, signerInfo, issuer: cert.issuerName, thumbprint: cert.thumbprint,
      validFrom: cert.validFrom, validTo: cert.validTo, hashAlgorithm: hashedAlgorithm ? 'ГОСТ Р 34.11-2012 512' : 'ГОСТ Р 34.11-2012 256',
    });
  } catch (e) { throw new Error(plain(e)); }
}
